// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useGoogleMaps from "@insite/client-framework/Common/Hooks/useGoogleMaps";
import { LocationGoogleMapMarkerType } from "@insite/client-framework/Common/Hooks/useLocationGoogleMarkers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import withDynamicGoogleMaps from "@insite/client-framework/Common/withDynamicGoogleMaps";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getDealerState } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import loadDealer from "@insite/client-framework/Store/Data/Dealers/Handlers/LoadDealer";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import {
    AdvancedTab,
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocationGoogleMap, { LocationGoogleMapStyles } from "@insite/content-library/Components/LocationGoogleMap";
import Page from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StyledBox, { StyledBoxStyles } from "@vanmeter/App/Components/Common/Box";
import RequestYourSpot from "@vanmeter/App/Components/Common/RequestYourSpot";
import XngZone from "@vanmeter/App/Components/Common/XngZone";
import Notify from "@vanmeter/App/Components/Notify";
import loadEvent from "@vanmeter/App/Handlers/EventList/LoadEvent";
import { IEventDetailsModel } from "@vanmeter/App/Services/EventListService";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { fontConfig } from "@vanmeter/XDL/constants";
import { encodedIconGeneralMapPin } from "@vanmeter/XDL/Icons/IconGeneralMapPin";
import { color, formatDate, xScreen } from "@vanmeter/XDL/Utilities";
import React, { useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    subTitle = "subTitle",
    summary = "summary",
    isFullWidth = "isFullWidth",
    categoryTargets = "categoryTargets",
    brandTargets = "brandTargets",
    pageTargets = "pageTargets",
    imagePublicId = "imagePublicId",
    imageAltText = "imageAltText",
    location = "location",
    eventDate = "eventDate",
    startTime = "startTime",
    endTime = "endTime",
    courseCode = "courseCode",
    cost = "cost",
    ceuEligibleCourse = "ceuEligibleCourse",
    isFeatured = "isFeatured",
    eventType = "eventType",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subTitle]: string;
        [fields.summary]: string;
        [fields.isFullWidth]: boolean;
        [fields.isFeatured]: boolean;
        [fields.categoryTargets]: string[];
        [fields.brandTargets]: string[];
        [fields.pageTargets]: string[];
        [fields.imagePublicId]: string;
        [fields.imageAltText]: string;
        [fields.location]: { displayName: string; value: string }[];
        [fields.eventDate]: Date;
        [fields.startTime]: string;
        [fields.endTime]: string;
        [fields.courseCode]: string;
        [fields.cost]: string;
        [fields.ceuEligibleCourse]: boolean;
        [fields.eventType]: string;
    };
    useLoadScript: ({ googleMapsApiKey }: { googleMapsApiKey: any }) => { isLoaded: boolean };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const googleMapsApiKey = getSettingsCollection(state).websiteSettings.googleMapsApiKey;
    return {
        googleMapsApiKey,
        isTabletScreen: state.xCommon.isTabletScreen,
        dealerState: getDealerState(state, props.fields.location?.[0]?.value || ""),
    };
};

const mapDispatchToProps = {
    loadEvent,
    loadDealer,
};

type Props = PageProps & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface VmiEventDetailsPageStyles {
    contentWrapper?: InjectableCss;
    locationInfoWrapper?: InjectableCss;
    locationInfo?: InjectableCss;
    locationMap?: InjectableCss;
    label?: TypographyProps;
    value?: TypographyProps;
    locationGoogleMap?: LocationGoogleMapStyles;
    locationGoogleMapTablet?: LocationGoogleMapStyles;
    styledBox?: StyledBoxStyles;
    rightSection?: InjectableCss;
    titleText?: TypographyProps;
    subTitleText?: TypographyProps;
}

export const vmiEventDetailsPageStyles: VmiEventDetailsPageStyles = {
    contentWrapper: {
        css: css`
            display: grid;
            grid-template-columns: 1fr minmax(0, 376px);
            ${xScreen("lte", "lg")} {
                grid-template-columns: 1fr minmax(0, 317px);
            }
            ${xScreen("lte", "md")} {
                grid-template-columns: 1fr 1fr;
            }
            ${xScreen("lt", "sm")} {
                grid-template-columns: 1fr;
            }
            grid-gap: 64px 22px;
        `,
    },
    rightSection: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 24px;
            height: fit-content;
        `,
    },
    styledBox: {
        container: {
            css: css`
                margin-bottom: 64px;
            `,
        },
        topStroke: {
            css: css`
                border-top: 8px solid ${color("primary")};
            `,
        },
        contentContainer: {
            css: css`
                padding: 16px;
            `,
        },
    },
    locationInfoWrapper: {
        css: css`
            display: flex;
            gap: 16px;
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    locationInfo: {
        css: css`
            flex: 1 1 40%;
            ${xScreen("lte", "md")} {
                flex: 1 1 100%;
            }
            display: flex;
            flex-direction: column;
            gap: 20px;
        `,
    },
    locationMap: {
        css: css`
            flex: 0 0 60%;
            ${xScreen("lte", "md")} {
                flex: 1 1 100%;
            }
        `,
    },
    label: {
        variant: "h5",
    },
    value: {
        ...fontConfig.regularSingleLine,
        as: "p",
        css: css`
            margin: 0;
            &.date {
                margin-bottom: 8px;
            }
        `,
    },
    locationGoogleMap: {
        googleMap: {
            zoom: 14,
            mapContainerStyle: {
                height: "316px",
                width: "100%",
            },
        },
    },
    locationGoogleMapTablet: {
        googleMap: {
            zoom: 14,
            mapContainerStyle: {
                height: "182px",
                width: "100%",
            },
        },
    },
    titleText: {
        variant: "h1",
    },
    subTitleText: {
        variant: "h4",
        css: css`
            &:empty {
                display: none;
            }
        `,
    },
};

const styles = vmiEventDetailsPageStyles;

const VmiEventDetailsPage: React.FunctionComponent<Props> = ({
    id,
    googleMapsApiKey,
    useLoadScript,
    isTabletScreen,
    loadEvent,
    fields,
    loadDealer,
    dealerState,
}) => {
    const shellContext = useShellContext();
    const dispatch = useDispatch();
    const [eventData, setEventData] = useState<IEventDetailsModel | undefined>();

    // Load event when NOT editing
    useEffect(() => {
        if (shellContext.isEditing) {
            const shouldLoadDealer = !dealerState.value && !dealerState.isLoading;
            if (shouldLoadDealer) {
                loadDealer({
                    id: fields.location?.[0]?.value,
                });
            }
        } else {
            loadEvent({
                pageId: id,
                onSuccess: event => {
                    dispatch({ type: "xEventDetailsPage/SetEvent", eventId: id, event });
                    setEventData(event);
                },
            });
        }
    }, [id, shellContext.isEditing]);

    // apply dealer data into event data when editing

    useEffect(() => {
        if (shellContext.isEditing) {
            setEventData({
                title: fields.title,
                subTitle: fields.subTitle,
                eventDate: formatDate(fields.eventDate),
                startTime: fields.startTime,
                endTime: fields.endTime,
                courseCode: fields.courseCode,
                cost: fields.cost,
                ceuEligible: fields.ceuEligibleCourse,
                type: fields.eventType,
                locationData: {
                    ...(dealerState.value as any),
                },
            });
        }
    }, [dealerState.value, shellContext.isEditing, fields, id]);

    const { isLoaded } = useLoadScript({ googleMapsApiKey });
    const { setGoogleMap, isGoogleMapsScriptsLoaded } = useGoogleMaps({
        isLoaded,
        isShown: true,
    });

    const mapMarkersElements = useMemo(
        () =>
            isGoogleMapsScriptsLoaded && eventData
                ? [
                      {
                          key: eventData.locationData.id,
                          locationId: eventData.locationData.id,
                          position: new google.maps.LatLng(
                              eventData.locationData.latitude,
                              eventData.locationData.longitude,
                          ),
                          type: "SELECTED" as LocationGoogleMapMarkerType,
                          icon: { url: encodedIconGeneralMapPin },
                          onClick: () => {},
                      },
                  ]
                : [],
        [isGoogleMapsScriptsLoaded, eventData],
    );

    return (
        <Page>
            <StyledWrapper {...styles.contentWrapper}>
                <StyledWrapper>
                    {shellContext.isEditing && <Notify type="info">{siteMessage("VMI_Apply_Indexing_Message")}</Notify>}
                    {eventData && (
                        <>
                            <Typography {...styles.titleText}>{eventData.title}</Typography>
                            <Typography {...styles.subTitleText}>{eventData?.subTitle}</Typography>
                            <StyledBox showTopStroke extendedStyles={styles.styledBox}>
                                <StyledWrapper {...styles.locationInfoWrapper}>
                                    <StyledWrapper {...styles.locationInfo}>
                                        {eventData?.ceuEligible && (
                                            <StyledWrapper>
                                                <Typography {...styles.label}>
                                                    {translate("CEU Eligible Course")}
                                                </Typography>
                                            </StyledWrapper>
                                        )}
                                        <StyledWrapper>
                                            <Typography {...styles.label}>{translate("Date & Time")}</Typography>
                                            <Typography {...styles.value} className="date">
                                                {eventData.eventDate}
                                            </Typography>
                                            <Typography {...styles.value}>
                                                {eventData.startTime} - {eventData.endTime}
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper>
                                            <Typography {...styles.label}>{translate("Location")}</Typography>
                                            <Typography {...styles.value}>{eventData.locationData.address1}</Typography>
                                            <Typography {...styles.value}>
                                                {eventData.locationData.city}, {eventData.locationData.state}
                                                {eventData.locationData.postalCode}
                                            </Typography>
                                        </StyledWrapper>
                                        {eventData.courseCode && (
                                            <StyledWrapper>
                                                <Typography {...styles.label}>{translate("Course Code")}</Typography>
                                                <Typography {...styles.value}>{eventData.courseCode}</Typography>
                                            </StyledWrapper>
                                        )}
                                        {eventData.cost && (
                                            <StyledWrapper>
                                                <Typography {...styles.label}>{translate("Price")}</Typography>
                                                <Typography {...styles.value}>{eventData.cost}</Typography>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.locationMap}>
                                        {isGoogleMapsScriptsLoaded && (
                                            <LocationGoogleMap
                                                extendedStyles={
                                                    isTabletScreen
                                                        ? styles.locationGoogleMapTablet
                                                        : styles.locationGoogleMap
                                                }
                                                currentLocation={mapMarkersElements[0].position}
                                                setGoogleMap={setGoogleMap}
                                                mapMarkerElements={mapMarkersElements}
                                                locationSearchFilter=""
                                                handleOpenLocationContent={() => {}}
                                            />
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledBox>
                        </>
                    )}
                    <XngZone contentId={id} zoneName="content04" hint="Event Summary" />
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    {eventData && (
                        <RequestYourSpot
                            eventName={eventData.title}
                            eventLocation={eventData.locationData.name}
                            eventDate={eventData.eventDate}
                        />
                    )}
                    <XngZone contentId={id} zoneName="content05" hint="Upcoming Events" />
                </StyledWrapper>
            </StyledWrapper>
        </Page>
    );
};
const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withDynamicGoogleMaps(VmiEventDetailsPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "Content",
        // OOTB bug: multi allowed parents not working - needed on VmiEventDetailsPage and VmiEventCoursePage
        // the issue on AddPage.tsx line 311 - should be -> this.props.addingPageUnderType.toLowerCase()
        // allowedParents: ["VmiEventsListPage"],
        fieldDefinitions: [
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            HideHeader,
            MetaDescription,
            MetaKeywords,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            {
                name: "horizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
            },
            {
                name: fields.title,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.subTitle,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
            },
            {
                name: fields.summary,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.isFullWidth,
                displayName: "Is Full Width Page",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: AdvancedTab,
            },
            {
                name: fields.isFeatured,
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
            {
                name: fields.imagePublicId,
                displayName: "Image",
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "General",
            },
            {
                name: fields.imageAltText,
                displayName: "Image Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "horizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
            },
            {
                name: fields.brandTargets,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.brand;
                },
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
                fieldDefinitions: [
                    {
                        name: "brand",
                        editorTemplate: "BlogEventsFieldEditor" as any,
                        isRequired: true,
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.categoryTargets,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.category;
                },
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
                fieldDefinitions: [
                    {
                        name: "category",
                        editorTemplate: "BlogEventsFieldEditor" as any,
                        isRequired: true,
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.pageTargets,
                editorTemplate: "ListField",
                fieldType: "General",
                defaultValue: [],
                getDisplay: (item: HasFields, state) => {
                    const {
                        url: { type, value },
                    } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    return value;
                },
                fieldDefinitions: [
                    {
                        name: "url",
                        editorTemplate: "LinkField",
                        isRequired: true,
                        allowCategories: () => false,
                        allowUrls: () => false,
                        defaultValue: {
                            type: "Page",
                            value: "",
                        },
                    },
                ],
            },
            {
                name: "horizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
            },

            {
                name: fields.location,
                editorTemplate: "BlogEventsFieldEditor" as any,
                options: [],
                isRequired: true,
                defaultValue: [],
                fieldType: "General",
                validate: (value: any) => {
                    if (Object.keys(value).length > 0) {
                        return "";
                    }

                    return "Please Select Location";
                },
            },
            {
                name: fields.eventDate,
                defaultValue: new Date(),
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.startTime,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.endTime,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "horizontalRule",
                defaultValue: "",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
            },
            {
                name: fields.courseCode,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
            },
            {
                name: fields.cost,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
            },
            {
                name: fields.ceuEligibleCourse,
                displayName: "CEU Eligible Course",
                defaultValue: false,
                editorTemplate: "CheckboxField",
                fieldType: "General",
            },
            {
                name: fields.eventType,
                editorTemplate: "SystemListDropDownField",
                systemListName: "EventType",
                defaultValue: "",
                isRequired: true,
                fieldType: "General",
            },
        ],
    },
};
export default pageModule;
/**
 * @deprecated Use string literal "VmiEventDetailsPage" instead of this constant.
 */
export const VmiEventDetailsPageContext = "VmiEventDetailsPage";
